import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NgSelectComponent } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { find, isEmpty, isNil, isNumber, isUndefined } from 'lodash';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { ValueLabelPair } from '@shared/interfaces/value-label-pair.interface';
import { DestroyService } from '@shared/services/destroy.service';
import { DeviceService } from '@shared/services/device/device.service';
import { LocalCacheService } from '@shared/services/local-cache.service';
import { filterNullish$ } from '@shared/utils/rxjs/filter-nullish.rxjs.util';
import { skipEqual$ } from '@shared/utils/rxjs/skip-equal.rxjs.util';
import { CustomControlAbstract } from '@ui-components/controls/custom-control.abstract';

import { RadioButtonBottomSheetComponent } from './radio-button-bottom-sheet/radio-button-bottom-sheet.component';

@UntilDestroy()
@Component({
  selector: 'app-radio-button-dropdown',
  templateUrl: './radio-button-dropdown.component.html',
  styleUrls: ['./radio-button-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [DestroyService],
})
export class RadioButtonDropdownComponent<T>
  extends CustomControlAbstract<any>
  implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor, OnDestroy
{
  @Input() formControl: FormControl;
  @Input() valueCss = '';
  @Input() optionCss = '';
  @Input() labelCss = '';
  @Input() containerWidthCss = '';
  @Input() containerCss = '';
  @Input() ngSelectCss = '';
  @Input() placeholderCss = '';
  @Input() label = '';
  @Input() labelTooltip = '';
  @Input() groupLabelBold = false;
  @Input() itemPlaceholder = 'item';
  @Input() tooltipShowDelay: number = null;
  @Input() icon: EIcon;
  @Input() iconTooltip = '';
  @Input() attrPlaceholder = '';
  @Input() filterGroupKey?: string;
  @Input() hasFade = true;
  @Input() onSelectValueContent: ValueLabelPair;
  // temporary for deprecated values (see MoveOutType)
  @Input() unsupportedValueLabel = 'select value';
  /*
   * issue available https://github.com/ng-select/ng-select/issues/2055
   * known workaround - https://github.com/Kyotu-Technology/noetic-frontend/pull/455
   */
  @Input() highlightWhenActive = false;
  @Input() attrAppendTo = '';
  @Input() allowSelectAll = false;
  @Input() isSmallFont = false;
  @Input() applySmallFontForCollapsed = true;
  @Input() isSmallPadding = false;
  @Input() multiSelect = false;
  @Input() attrDisable = false;
  @Input() attrDisableStyling = true;
  @Input() isLabelInside = false;
  @Input() markAsInvalid = false;
  @Input() allowClear = true;
  @Input() allowSearch = true;
  @Input() displayLabelAsValue = false;
  @Input() labelRequired: boolean;
  @Input() showEmptyValue = false;
  @Input() showValueTooltip = false;
  @Input() activeOnFocus = false;
  @Input() errorSection = true;
  // TODO: move autoSortList to setter
  // otherwise parameters order matters
  @Input() autoSortList = true;
  @Input() dividerPosition!: number;
  @Input() allPropertiesLabel = false;
  @Input() displaySkeleton = false;
  @Input() scrollableElementId: string;
  @Input() scrollableElementIds: string[];
  @Input() allowAngelBrackets = false;
  @Input() allPropertiesPlaceholder!: string;
  @Input() portfolioPropertiesPlaceholder!: string;
  @Input() showSwitch = false;
  @Input() switchStatus = false;
  @Input() optionWithIcon = false;
  @Input() withNoFadeOption = false;
  @Input() preventChangesWhileOpened = false;

  @Input()
  set items(items: IRadioButtonOption<T>[] | IRadioButtonOption<T>[][] | null) {
    this._items = items || [];
  }
  get items() {
    return this._items;
  }

  @Input() set listLoading(listLoading: boolean) {
    this._listLoading = listLoading;
    this.cdr.markForCheck();
  }

  @Input() set onOpenSelector(openSelector: boolean) {
    setTimeout(() => {
      if (openSelector && this.ngSelect) this.ngSelect.open();
    });
  }

  @Input() customValueTemplate: TemplateRef<any>;
  @Input() customListItemTemplate?: TemplateRef<any>;
  @Input() allowAddNew = false;
  @Input() addNewAtTop = false;
  @Input() addNewLabel = '+ Add new item';
  @Input() clearOption = false;
  @Input() suppressInternalLabel = false;
  @Input() skipNormalizeOnChange = false;

  @Output() addNewClicked = new EventEmitter();
  @Output() switchToggled = new EventEmitter();

  EColorPalette = EColorPalette;
  displayValue = '';
  controlItems: IRadioButtonOption<T>[] = [];
  controlSectionItems: IRadioButtonOption<T>[][] = [];
  transformValueList: IRadioButtonOption<T>[] = [];
  hasValue = false;
  _labelWidth$ = new BehaviorSubject<number>(null);
  labelWidth$ = this._labelWidth$.asObservable();
  paddingLeft$!: Observable<string>;
  private _items: IRadioButtonOption<T>[] | IRadioButtonOption<T>[][] = [];
  _listLoading = false;
  labelWidthN: number;
  _labelElement: ElementRef<HTMLElement>;
  _focused = false;
  isOpen = false;

  @ViewChild(NgSelectComponent) ngSelect: NgSelectComponent;

  private _resizeObserver: ResizeObserver;

  public isMobileDevice = false;

  @ViewChild('labelElement', { static: false }) set content(value: ElementRef<HTMLElement>) {
    if (!value) return;

    this._labelElement = value;

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    this._resizeObserver = new ResizeObserver(entries => {
      const labelWidth = entries[0]?.contentRect?.width;
      if (labelWidth) {
        this.labelWidthN = labelWidth;
        this._labelWidth$.next(labelWidth);
        this.cdr.markForCheck();
      }
    });

    this._resizeObserver.observe(value.nativeElement);
  }

  constructor(
    @Self() @Optional() protected ngControl: NgControl,
    protected cdr: ChangeDetectorRef,
    @Optional() formDirective: FormGroupDirective,
    private cache: LocalCacheService,
    private destroy$: DestroyService,
    private bottomSheet: MatBottomSheet,
    private deviceService: DeviceService,
    private viewContainerRef: ViewContainerRef
  ) {
    super(ngControl, cdr, formDirective);
  }

  ngOnInit(): void {
    this.initControl();
    this.initPaddingLeft();

    this.deviceService.isMobile$
      .pipe(untilDestroyed(this))
      .subscribe(isMobile => {
        this.isMobileDevice = isMobile;
        if (isMobile && this.ngSelect) {
          this.ngSelect.close();
        }
      })
      .untilDestroyed(this);
  }

  ngAfterViewInit() {
    this.initPaddingLeft();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formControl'] && this.formControl !== this.control) {
      this.control = this.formControl;
      this.initControl();
    }

    if (('items' in changes || 'autoSortList' in changes) && (!this.preventChangesWhileOpened || !this.isOpen)) {
      this.processItems();
    }
  }

  private processItems(): void {
    this.controlSectionItems = [];
    this.controlItems = [];
    this.transformValueList = [];

    if (Array.isArray(this.items?.[0])) {
      this.controlSectionItems = (this.items as IRadioButtonOption<T>[][]).map(section => this.sortSection(section));
      this.transformValueList = this.controlSectionItems.flat();
    } else {
      this.controlItems = this.sortSection(this.items as IRadioButtonOption<T>[]);
      this.transformValueList = [...this.controlItems];
    }

    this.transformValue();
  }

  get isShowDisplayValue() {
    if (!this.allowSearch) return true;
    return !this._focused;
  }

  searchFunction(term: string, value: T): boolean {
    term = term.toLowerCase();
    const item = find(this.transformValueList, { value }) as IRadioButtonOption<T>;

    return (
      item?.label?.toLowerCase()?.indexOf(term) > -1 ||
      (item?.additionalSearchLabel && item.additionalSearchLabel.toLowerCase()?.indexOf(term) > -1)
    );
  }

  writeValue(value: any): void {
    if (value != this.control.value) {
      this.control.setValue(value);
    }
    this.transformValue();
  }

  onSelectChange(event: any): void {
    if (this.skipNormalizeOnChange) {
      this.onChanged(event);
    } else {
      this.normalizeControlValue();
      this.onChanged(this.control.value);
    }
  }

  private normalizeControlValue(): void {
    const value = this.control.value;
    if (Array.isArray(value)) {
      let hasChanges = false;
      const normalized = value.map(v => {
        if (typeof v === 'object' && v !== null && 'value' in v) {
          hasChanges = true;
          return v.value;
        }
        return v;
      });

      if (hasChanges) {
        this.control.setValue(normalized, { emitEvent: false });
      }
    }
  }

  onSelectAll() {
    this.control.patchValue(this.transformValueList.map(t => t.value));
    this.cdr.markForCheck();
  }

  onClearAll() {
    this.control.patchValue([]);
    this.cdr.markForCheck();
  }

  onAddNew() {
    this.addNewClicked.emit();
    this.ngSelect.close();
    this.cdr.markForCheck();
  }

  onToggleSwitch(collectionsSelected: boolean) {
    this.switchToggled.emit(collectionsSelected);
    this.ngSelect.close();
    this.cdr.markForCheck();
  }

  onClear() {
    this.transformValue();
  }

  onOpen() {
    this.isOpen = true;
    if (this.multiSelect) {
      if (this.controlSectionItems?.length) {
        this.items = this.controlSectionItems;
      } else if (this.controlItems?.length) {
        this.items = this.controlItems;
      }
    }

    if (this.scrollableElementIds?.length) this.toggleScrollableElementScroll('hidden', '5.4px');
  }

  onClose(): void {
    this.isOpen = false;
    if (this.scrollableElementIds?.length) this.toggleScrollableElementScroll('auto', '0px');
  }

  handleDropdownClick($event: MouseEvent): void {
    const isBottomSheetOpened = !!this.bottomSheet._openedBottomSheetRef;
    if (this.isMobileDevice && !isBottomSheetOpened) {
      $event.preventDefault();
      $event.stopPropagation();
      if (this.ngSelect) {
        this.ngSelect.close();
      }

      const sections = Array.isArray(this._items?.[0]) ? (this._items as IRadioButtonOption<T>[][]) : [];

      const items = !Array.isArray(this._items?.[0]) ? (this._items as IRadioButtonOption<T>[]) : [];

      const bottomSheetRef = this.bottomSheet.open(RadioButtonBottomSheetComponent, {
        data: {
          sections: sections.map(section => this.sortSection(section)),
          items: this.sortSection(items),
          selectedValue: this.control.value,
          label: this.attrPlaceholder,
          allowSearch: this.allowSearch,
          multiSelect: this.multiSelect,
          allowAddNew: this.allowAddNew,
          addNewLabel: this.addNewLabel,
          autoFocus: false,
          onSearch: (term: string) => this.handleBottomSheetSearch(term, sections, items),
        },
        hasBackdrop: true,
        backdropClass: 'bottom-sheet-above-modal-backdrop',
        viewContainerRef: this.viewContainerRef,
        panelClass: 'bottom-sheet-above-modal',
      });

      bottomSheetRef
        .afterDismissed()
        .pipe(untilDestroyed(this))
        .subscribe(result => {
          if (result === 'add_new') {
            this.addNewClicked.emit();
          } else if (result !== undefined && result !== null) {
            this.control.setValue(result);
            this.cdr.markForCheck();
          }
          if (this.ngSelect) {
            this.ngSelect.blur();
          }
        })
        .untilDestroyed(this);
    } else {
      const target = $event.target as HTMLElement;
      if (target.tagName === 'NG-SELECT') {
        this.ngSelect.toggle();
      }
    }
  }

  private handleBottomSheetSearch(
    term: string,
    sections: IRadioButtonOption<T>[][],
    items: IRadioButtonOption<T>[]
  ): IRadioButtonOption<T>[] {
    const searchTerm = term.toLowerCase();
    if (sections?.length) {
      return sections
        .flat()
        .filter(
          item =>
            item.label.toLowerCase().includes(searchTerm) ||
            (item.additionalSearchLabel && item.additionalSearchLabel.toLowerCase().includes(searchTerm))
        );
    } else {
      return items.filter(
        item =>
          item.label.toLowerCase().includes(searchTerm) ||
          (item.additionalSearchLabel && item.additionalSearchLabel.toLowerCase().includes(searchTerm))
      );
    }
  }

  compareFn(item: unknown, selected: unknown): boolean {
    if (Array.isArray(selected)) {
      return selected.includes(item);
    } else {
      return selected === item;
    }
  }

  private sortSection(items: IRadioButtonOption<T>[]) {
    return items?.length && this.autoSortList
      ? [...items].sort((a, b) => {
          if (this.multiSelect) {
            const aSelected = this.control.value?.includes(a.value);
            const bSelected = this.control.value?.includes(b.value);
            if (aSelected != bSelected) {
              return aSelected < bSelected ? 1 : -1;
            }
          }

          if (isNumber(a.value) && isNumber(b.value)) {
            if (a.value < 0 || b.value < 0) {
              if (a.value < 0 && b.value >= 0) return -1;
              if (b.value < 0 && a.value >= 0) return 1;
            }
          }

          if (a.sort != b.sort) {
            if (typeof b.sort === 'undefined') return -1;
            if (typeof a.sort === 'undefined') return 1;
            return a.sort > b.sort ? 1 : -1;
          }
          return a.label?.localeCompare(b.label, undefined, { sensitivity: 'accent' });
        })
      : items || [];
  }

  private initControl() {
    this.initControlBase();

    this.transformValue();
    this.control.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(value => this.transformValue())
      .untilDestroyed(this);
  }

  private initLabelWidth() {
    if (!this.isLabelInside || !this._labelElement) return;

    this.labelWidthN = this._labelElement?.nativeElement?.clientWidth;
    const width = this._labelElement?.nativeElement?.clientWidth;
    width && this._labelWidth$.next(width);

    this.cdr.markForCheck();
  }

  private transformValue() {
    const value = this.control?.value;
    const hasValue = Array.isArray(value) ? !isEmpty(value) : !isNil(value);
    if (hasValue || this.showEmptyValue) {
      this.hasValue = true;
      if (Array.isArray(value)) {
        if (this.displayLabelAsValue) {
          this.displayValue = this.transformValueList
            .filter(x => value.includes(x.value))
            .map(x => x.label)
            ?.join(', ');
        } else {
          if (this.allPropertiesLabel && value?.length === this.controlItems?.length) {
            this.displayValue = this.allPropertiesPlaceholder
              ? this.allPropertiesPlaceholder
              : this.switchStatus
              ? 'All Property Groups'
              : 'All Properties';
          } else if (value?.length > 1) {
            this.displayValue = this.onSelectValueContent
              ? this.getCustomTransformValue(value?.length)
              : `${value.length} ${this.pluralizeWord(this.itemPlaceholder, value?.length)}`;
          } else {
            const displayValue = this.transformValueList?.find(item => item.value === value[0])?.label;
            this.displayValue = isUndefined(displayValue) ? this.unsupportedValueLabel : displayValue;
          }
        }
      } else {
        const valueStr = JSON.stringify(value);
        const item = this.transformValueList?.find(
          item => item.value === value || JSON.stringify(item.value) === valueStr
        );
        let displayValue = item?.displayValue || item?.label;

        if (this.allPropertiesPlaceholder && valueStr === '-1') displayValue = this.allPropertiesPlaceholder;

        if (this.portfolioPropertiesPlaceholder && valueStr === '-2')
          displayValue = this.portfolioPropertiesPlaceholder;

        this.displayValue = isUndefined(displayValue) ? this.unsupportedValueLabel : displayValue;
      }
    } else {
      this.hasValue = false;
    }

    this.cdr.detectChanges();
  }

  private pluralizeWord(word: string, count: number): string {
    if (count > 1) {
      if (word.endsWith('y')) {
        return word.slice(0, -1) + 'ies';
      } else if (
        word.endsWith('s') ||
        word.endsWith('x') ||
        word.endsWith('z') ||
        word.endsWith('ch') ||
        word.endsWith('sh')
      ) {
        return word + 'es';
      } else if (word.endsWith('f')) {
        return word.slice(0, -1) + 'ves';
      } else if (word.endsWith('fe')) {
        return word.slice(0, -2) + 'ves';
      } else if (word.endsWith('o') && !/(photo|piano|halo)$/.test(word)) {
        return word + 'es';
      } else if (word.endsWith('us')) {
        return word.slice(0, -2) + 'i';
      } else {
        return word + 's';
      }
    }
    return word;
  }

  private getCustomTransformValue(count: number): string {
    if (!count) {
      return '';
    }

    return (count > 1 ? this.onSelectValueContent.multiple : this.onSelectValueContent.single).replace(
      this.onSelectValueContent.mask,
      `${count}`
    );
  }

  private initPaddingLeft(): void {
    const labelWidth$ = this.labelWidth$.pipe(
      untilDestroyed(this),
      filterNullish$(false),
      skipEqual$(),
      tap(currentLabelWidth => {
        if (this.filterGroupKey) {
          const groupOffset = this.cache.get(this.filterGroupKey);
          if ((groupOffset as number) < currentLabelWidth) {
            this.cache.set(this.filterGroupKey, currentLabelWidth, {
              lsKey: this.filterGroupKey,
            });
          }
        }
      })
    );

    const labelGroupWidth$ = this.filterGroupKey
      ? this.cache
          .get$(this.filterGroupKey, this.destroy$, {
            initialState: 0,
            lsKey: this.filterGroupKey,
          })
          .pipe(untilDestroyed(this), skipEqual$())
      : of(null);

    this.paddingLeft$ = combineLatest([labelWidth$, labelGroupWidth$]).pipe(
      untilDestroyed(this),
      map(([labelWidth, groupWidth]) => Math.max(labelWidth, groupWidth)),
      filterNullish$(false),
      map(width => (this.isLabelInside && width ? width + 'px' : 'initial')),
      skipEqual$()
    );
  }

  private toggleScrollableElementScroll(overflowY: string, marginRight: string): void {
    this.scrollableElementIds.forEach(id => {
      const viewport = document.getElementById(id) as HTMLElement;
      if (viewport && (overflowY === 'auto' || viewport.scrollHeight > viewport.clientHeight)) {
        viewport.style.overflowY = overflowY;
        viewport.style.marginRight = marginRight;
      }
    });
  }

  onClearSelected() {
    this.control.patchValue(null);
    this.ngSelect.close();
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }
}
